import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Edit, Download, Calendar, User, TrendingUp, Users, Eye, Target, DollarSign, BarChart3, Share2, Copy, X } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { MetricCard } from '../components/reports/MetricCard';
import { CategoryMarketSharePieCharts } from '../components/reports/CategoryMarketSharePieCharts';
import html2pdf from 'html2pdf.js';

type CampaignReport = {
  id: string;
  brandId: string;
  campaignName: string;
  reportType: 'new-brand' | 'existing-brand' | 'mid-campaign';
  campaignStartDate: string;
  campaignEndDate?: string;
  reportDate?: string;
  status: 'draft' | 'published' | 'archived';
  createdAt: string;
  updatedAt: string;
  shareToken?: string | null;
  sharePassword?: string | null;
  Brand?: {
    id: string;
    name: string;
  };
  creator?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  reportData?: string; // JSON string
};

interface ReportData {
  // Campaign Overview
  campaignName?: string;
  brandId?: string;
  reportDate?: string;
  campaignStartDate?: string;
  campaignEndDate?: string;
  daysElapsed?: string;
  daysRemaining?: string;
  campaignId?: string;
  
  // Media Performance
  totalImpressions?: string;
  averageImpressionsPerMachine?: string;
  targetImpressions?: string;
  doohAdPlays?: string;
  averagePlaysPerMachine?: string;
  totalScreenTimeMinutes?: string;
  homepageBannerImpressions?: string;
  categoryBannerImpressions?: string;
  clickThroughRate?: string;
  productPageViews?: string;
  engagementRate?: string;
  
  // Sales Performance
  overallSalesPercentIncrease?: string;
  products?: Array<{ id: string; name: string; percentIncrease: string }>;
  
  // Category Performance - Market Share by Product
  preCampaignMarketShareProducts?: Array<{ id: string; name: string; marketShare: string }>;
  postCampaignMarketShareProducts?: Array<{ id: string; name: string; marketShare: string }>;
  currentMarketShareProducts?: Array<{ id: string; name: string; marketShare: string }>;
  
  // Demographics
  genderMale?: string;
  genderFemale?: string;
  age1824?: string;
  age2534?: string;
  age3544?: string;
  age4554?: string;
  age55Plus?: string;
  peakHours?: string;
  peakDays?: string;
  purchaseTimingMorning?: string;
  purchaseTimingAfternoon?: string;
  purchaseTimingEvening?: string;
  
  // Campaign Pacing & Budget
  totalCampaignBudget?: string;
  percentCampaignElapsed?: string;
  percentBudgetUtilized?: string;
  pacingStatus?: 'on-track' | 'ahead' | 'behind';
  
  // Key Insights
  keyInsights?: string;
}

export default function AdminManualReportView() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [isGenericMode, setIsGenericMode] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const reportContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) {
      loadReport();
    }
    checkSuperAdmin();
  }, [id]);

  async function checkSuperAdmin() {
    try {
      const res = await fetch('/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const roles: string[] = data.roles || [];
        setIsSuperAdmin(roles.includes('superadmin'));
      }
    } catch (error) {
      console.error('Error checking super admin status:', error);
    }
  }

  async function loadReport() {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/reports/${id}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setReport(data);
        
        // Parse reportData JSON
        if (data.reportData) {
          try {
            const parsed = JSON.parse(data.reportData);
            setReportData(parsed);
          } catch (e) {
            console.error('Failed to parse reportData:', e);
          }
        }
      } else {
        alert('Failed to load report');
        navigate('/admin/reports');
      }
    } catch (error) {
      console.error('Failed to load report:', error);
      alert('Failed to load report');
      navigate('/admin/reports');
    } finally {
      setLoading(false);
    }
  }

  async function handlePublish() {
    if (!id) return;
    try {
      const res = await fetch(`/api/admin/reports/${id}/publish`, {
        method: 'PUT',
        credentials: 'include',
      });
      if (res.ok) {
        loadReport();
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to publish report');
      }
    } catch (error) {
      console.error('Failed to publish report:', error);
      alert('Failed to publish report');
    }
  }

  async function handleExportPDF() {
    if (!report) {
      alert('Report not loaded. Please wait and try again.');
      return;
    }
    
    if (!reportData) {
      alert('Report data not available. Cannot export PDF.');
      return;
    }
    
    if (!reportContentRef.current) {
      alert('Report content not found. Please try again.');
      return;
    }
    
    try {
      // Hide header buttons during export
      const headerElement = document.getElementById('report-header');
      const originalDisplay = headerElement?.style.display || '';
      if (headerElement) {
        headerElement.style.display = 'none';
      }
      
      const element = reportContentRef.current;
      const campaignNameForExport = isGenericMode ? 'Retail-Media-Campaign' : (report.campaignName || 'Campaign-Report');
      const opt = {
        margin: [15, 15, 15, 15] as [number, number, number, number],
        filename: `${campaignNameForExport}-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          backgroundColor: '#ffffff',
        },
        jsPDF: { 
          unit: 'mm' as const, 
          format: 'a4' as const, 
          orientation: 'portrait' as const,
          compress: true,
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] as any }
      };
      
      await html2pdf().set(opt).from(element).save();
      
      // Restore header visibility
      if (headerElement) {
        headerElement.style.display = originalDisplay;
      }
    } catch (error: any) {
      console.error('Failed to export PDF:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Failed to export PDF: ${error.message || 'Unknown error'}. Please check the console for details.`);
      
      // Restore header visibility on error
      const headerElement = document.getElementById('report-header');
      if (headerElement) {
        headerElement.style.display = '';
      }
    }
  }

  async function handleGenerateShareLink() {
    if (!id) return;
    try {
      const res = await fetch(`/api/admin/reports/${id}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ password: sharePassword || undefined }),
      });

      if (res.ok) {
        const data = await res.json();
        const fullUrl = `${window.location.origin}${data.shareUrl}`;
        setShareLink(fullUrl);
        setSharePassword(''); // Clear password field after generating
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to generate share link');
      }
    } catch (error) {
      console.error('Failed to generate share link:', error);
      alert('Failed to generate share link');
    }
  }

  async function handleRevokeShareLink() {
    if (!id) return;
    try {
      const res = await fetch(`/api/admin/reports/${id}/share`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (res.ok) {
        setShareLink(null);
        setSharePassword('');
        loadReport();
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to revoke share link');
      }
    } catch (error) {
      console.error('Failed to revoke share link:', error);
      alert('Failed to revoke share link');
    }
  }

  function handleCopyLink() {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2000);
    }
  }

  useEffect(() => {
    if (report?.shareToken) {
      const fullUrl = `${window.location.origin}/reports/shared/${report.shareToken}`;
      setShareLink(fullUrl);
    }
  }, [report]);

  if (loading) {
    return <div className="container mx-auto p-6">Loading...</div>;
  }

  if (!report) {
    return <div className="container mx-auto p-6">Report not found</div>;
  }

  const data = reportData || {};

  // Helper functions for generic mode
  const getBrandName = () => {
    if (isGenericMode) return 'Brand Partner';
    return report.Brand?.name || '';
  };

  // Create a consistent product name mapping for generic mode
  const getProductNameMap = () => {
    if (!isGenericMode) return new Map<string, string>();
    
    const productMap = new Map<string, string>();
    const allProducts = [
      ...(data.products || []),
      ...(data.preCampaignMarketShareProducts || []),
      ...(data.currentMarketShareProducts || []),
      ...(data.postCampaignMarketShareProducts || [])
    ];
    
    const uniqueProducts = Array.from(
      new Set(allProducts.map(p => p.id).filter(Boolean))
    );
    
    uniqueProducts.forEach((id, index) => {
      productMap.set(id, `Product ${index + 1}`);
    });
    
    return productMap;
  };

  const productNameMap = getProductNameMap();

  const getProductName = (product: { id: string; name: string }, index?: number) => {
    if (isGenericMode) {
      if (product.id && productNameMap.has(product.id)) {
        return productNameMap.get(product.id)!;
      }
      return `Product ${index !== undefined ? index + 1 : 1}`;
    }
    return product.name || 'Unnamed Product';
  };

  const getCampaignName = () => {
    if (isGenericMode) return 'Retail Media Campaign';
    return report.campaignName || 'Campaign Overview';
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header - Hidden during PDF export */}
      <div className="flex items-center justify-between mb-6" id="report-header">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/reports')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {(report.status === 'draft' || (report.status === 'published' && isSuperAdmin)) && (
            <>
              <Button variant="outline" onClick={() => navigate(`/admin/reports/${id}/edit`)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              {report.status === 'draft' && (
                <Button onClick={handlePublish}>
                  Publish Report
                </Button>
              )}
              {report.status === 'published' && isSuperAdmin && (
                <Button onClick={handlePublish}>
                  Republish Report
                </Button>
              )}
            </>
          )}
          <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Share Report</DialogTitle>
                <DialogDescription>
                  Generate a shareable link for this report. You can optionally set a password to protect access.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {shareLink ? (
                  <>
                    <div className="space-y-2">
                      <Label>Shareable Link</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          value={shareLink}
                          readOnly
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={handleCopyLink}
                        >
                          {shareLinkCopied ? (
                            <X className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      {shareLinkCopied && (
                        <p className="text-sm text-green-600">Link copied to clipboard!</p>
                      )}
                    </div>
                    <Button
                      variant="destructive"
                      onClick={handleRevokeShareLink}
                      className="w-full"
                    >
                      Revoke Share Link
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="share-password">Password (Optional)</Label>
                      <Input
                        id="share-password"
                        type="password"
                        value={sharePassword}
                        onChange={(e) => setSharePassword(e.target.value)}
                        placeholder="Leave empty for no password"
                      />
                      <p className="text-sm text-muted-foreground">
                        If set, viewers will need to enter this password to access the report.
                      </p>
                    </div>
                    <Button
                      onClick={handleGenerateShareLink}
                      className="w-full"
                    >
                      Generate Share Link
                    </Button>
                  </>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="generic-mode" 
                checked={isGenericMode}
                onCheckedChange={(checked) => setIsGenericMode(checked === true)}
              />
              <Label htmlFor="generic-mode" className="text-sm cursor-pointer">
                Generic Report
              </Label>
            </div>
            <Button variant="outline" onClick={handleExportPDF}>
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Report Content - This will be exported */}
      <div ref={reportContentRef} className="report-content">
        {/* Campaign Overview */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">{getCampaignName()}</CardTitle>
          </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-muted-foreground mb-1">Campaign Dates</div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {report.campaignStartDate ? new Date(report.campaignStartDate).toLocaleDateString() : 'N/A'}
                  {report.campaignEndDate ? ` - ${new Date(report.campaignEndDate).toLocaleDateString()}` : ''}
                </span>
              </div>
            </div>
            {data.reportDate && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Report Date</div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(data.reportDate).toLocaleDateString()}</span>
                </div>
              </div>
            )}
            {data.daysElapsed && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Days Elapsed</div>
                <div className="text-2xl font-bold">{data.daysElapsed}</div>
              </div>
            )}
            {data.daysRemaining && (
              <div>
                <div className="text-sm text-muted-foreground mb-1">Days Remaining</div>
                <div className="text-2xl font-bold">{data.daysRemaining}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Media Performance */}
      {(data.totalImpressions || data.homepageBannerImpressions || data.categoryBannerImpressions || data.productPageViews || data.doohAdPlays || data.averagePlaysPerMachine || data.totalScreenTimeMinutes) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Media Performance</CardTitle>
            <CardDescription>Impressions and digital engagement metrics</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {data.totalImpressions && (
                <MetricCard
                  title="Total Impressions"
                  value={parseInt(data.totalImpressions)}
                  label="Impressions"
                />
              )}
              
              {data.homepageBannerImpressions && (
                <MetricCard
                  title="Home Banner Ad Views"
                  value={parseInt(data.homepageBannerImpressions)}
                  label="Views"
                />
              )}
              
              {data.categoryBannerImpressions && (
                <MetricCard
                  title="Category Banner Ad Views"
                  value={parseInt(data.categoryBannerImpressions)}
                  label="Views"
                />
              )}
              
              {data.productPageViews && (
                <div>
                  <div className="text-sm text-muted-foreground mb-2">Product Page Views</div>
                  <div className="text-3xl font-bold">{parseInt(data.productPageViews).toLocaleString()}</div>
                </div>
              )}
            </div>
            
            {/* Total Ad Playout (To-Date) */}
            {(data.doohAdPlays || data.averagePlaysPerMachine || data.totalScreenTimeMinutes) && (
              <>
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 text-foreground">Total Ad Playout (To-Date)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {data.doohAdPlays && (
                      <MetricCard
                        title="DOOH Ad Plays"
                        value={parseInt(data.doohAdPlays)}
                        label="Plays"
                      />
                    )}
                    {data.averagePlaysPerMachine && (
                      <MetricCard
                        title="Customer Dwell Time"
                        value={parseInt(data.averagePlaysPerMachine)}
                        label="Hours"
                      />
                    )}
                    {data.totalScreenTimeMinutes && (
                      <MetricCard
                        title="Total Screen Time"
                        value={parseInt(data.totalScreenTimeMinutes)}
                        label="Hours"
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Key Metrics Grid */}
      {data.clickThroughRate && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Click-Through Rate</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{parseFloat(data.clickThroughRate).toFixed(2)}%</div>
              <p className="text-xs text-muted-foreground mt-1">CTR performance</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sales Performance */}
      {data.overallSalesPercentIncrease && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Sales Performance</CardTitle>
            <CardDescription>Unit sales metrics and product breakdown</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <div className="text-sm text-muted-foreground">Overall Sales Increase</div>
                <div className="text-3xl font-bold text-green-600">
                  +{parseFloat(data.overallSalesPercentIncrease).toFixed(1)}%
                </div>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
            
            {data.products && data.products.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Product Performance</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.products.map((product, index) => (
                    <Card key={product.id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm text-muted-foreground mb-1">{getProductName(product, index)}</div>
                            <div className="text-2xl font-bold text-green-600">
                              {product.percentIncrease ? `+${parseFloat(product.percentIncrease).toFixed(1)}%` : 'N/A'}
                            </div>
                          </div>
                          {product.percentIncrease && (
                            <TrendingUp className="h-8 w-8 text-green-600" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Category Performance - Market Share Pie Charts */}
      {data.preCampaignMarketShareProducts && data.preCampaignMarketShareProducts.length > 0 && (
        <CategoryMarketSharePieCharts
          preCampaignProducts={data.preCampaignMarketShareProducts.map((p) => ({
            id: p.id,
            name: getProductName(p),
            marketShare: parseFloat(p.marketShare) || 0,
          }))}
          currentProducts={(data.currentMarketShareProducts || data.postCampaignMarketShareProducts || data.preCampaignMarketShareProducts).map((p) => ({
            id: p.id,
            name: getProductName(p),
            marketShare: parseFloat(p.marketShare) || 0,
          }))}
          brandName={getBrandName() || 'Brand'}
        />
      )}

      {/* Demographics */}
      {(data.genderMale || data.age1824 || data.peakHours) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Demographics Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {(data.genderMale || data.genderFemale) && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Gender Split</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.genderMale && (
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">{parseFloat(data.genderMale).toFixed(1)}%</div>
                      <div className="text-sm text-muted-foreground">Male</div>
                    </div>
                  )}
                  {data.genderFemale && (
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">{parseFloat(data.genderFemale).toFixed(1)}%</div>
                      <div className="text-sm text-muted-foreground">Female</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {(data.age1824 || data.age2534 || data.age3544) && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Age Distribution</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {data.age1824 && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age1824).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">18-24</div>
                    </div>
                  )}
                  {data.age2534 && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age2534).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">25-34</div>
                    </div>
                  )}
                  {data.age3544 && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age3544).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">35-44</div>
                    </div>
                  )}
                  {data.age4554 && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age4554).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">45-54</div>
                    </div>
                  )}
                  {data.age55Plus && (
                    <div className="text-center">
                      <div className="text-xl font-bold">{parseFloat(data.age55Plus).toFixed(1)}%</div>
                      <div className="text-xs text-muted-foreground">55+</div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {(data.peakHours || data.peakDays || data.purchaseTimingMorning) && (
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Purchase Timing</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {data.peakHours && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Peak Hours</div>
                      <div className="font-semibold">{data.peakHours}</div>
                    </div>
                  )}
                  {data.peakDays && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Peak Days</div>
                      <div className="font-semibold">{data.peakDays}</div>
                    </div>
                  )}
                  {(data.purchaseTimingMorning || data.purchaseTimingAfternoon || data.purchaseTimingEvening) && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-2">Time Distribution</div>
                      <div className="space-y-1">
                        {data.purchaseTimingMorning && (
                          <div className="flex justify-between">
                            <span>Morning:</span>
                            <span className="font-semibold">{parseFloat(data.purchaseTimingMorning).toFixed(1)}%</span>
                          </div>
                        )}
                        {data.purchaseTimingAfternoon && (
                          <div className="flex justify-between">
                            <span>Afternoon:</span>
                            <span className="font-semibold">{parseFloat(data.purchaseTimingAfternoon).toFixed(1)}%</span>
                          </div>
                        )}
                        {data.purchaseTimingEvening && (
                          <div className="flex justify-between">
                            <span>Evening:</span>
                            <span className="font-semibold">{parseFloat(data.purchaseTimingEvening).toFixed(1)}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Campaign Pacing & Budget */}
      {(data.totalCampaignBudget || data.percentCampaignElapsed || data.percentBudgetUtilized) && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Campaign Pacing & Budget</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.totalCampaignBudget && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Total Campaign Budget</div>
                  <div className="text-2xl font-bold">£{parseFloat(data.totalCampaignBudget).toLocaleString()}</div>
                </div>
              )}
              {data.percentCampaignElapsed && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Campaign Elapsed</div>
                  <div className="text-2xl font-bold">{parseFloat(data.percentCampaignElapsed).toFixed(1)}%</div>
                  <Progress value={parseFloat(data.percentCampaignElapsed)} className="mt-2" />
                </div>
              )}
              {data.percentBudgetUtilized && (
                <div>
                  <div className="text-sm text-muted-foreground mb-1">Budget Utilized</div>
                  <div className="text-2xl font-bold">{parseFloat(data.percentBudgetUtilized).toFixed(1)}%</div>
                  <Progress value={parseFloat(data.percentBudgetUtilized)} className="mt-2" />
                </div>
              )}
            </div>
            {data.pacingStatus && (
              <div>
                <div className="text-sm text-muted-foreground mb-2">Pacing Status</div>
                <Badge 
                  variant={
                    data.pacingStatus === 'ahead' ? 'default' : 
                    data.pacingStatus === 'behind' ? 'destructive' : 
                    'secondary'
                  }
                >
                  {data.pacingStatus === 'on-track' ? 'On Track' : 
                   data.pacingStatus === 'ahead' ? 'Ahead' : 
                   'Behind'}
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Key Insights */}
      {data.keyInsights && (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">Key Insights & Observations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none whitespace-pre-wrap">{data.keyInsights}</div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}

